import React, { useEffect, useState } from "react";
import "../../../css/Common/Components/ContactUs/TimeSelector.css";
import TimeSlots from "./TimeSlots";
import { db } from "../../../backend/firebaseConfig";
import { collection, query, where, getDocs } from "firebase/firestore";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";

/**
 * TimeSelector component allows users to select a time slot for a given date.
 * It displays available 30-minute time slots between 09:00 and 16:30, 
 * disables slots that are already booked (fetched from Firestore), 
 * and notifies the parent component of the selected time.
 *
 */
const TimeSelector = ({
  selectedDate,
  selectedTime,
  setSelectedTime,
  onClose,
}) => {
  const [disabledSlots, setDisabledSlots] = useState([]);

  /**
   * Generates an array of 30-minute time slot strings between 09:00 and 16:30.
   *
   * Each slot is formatted as "HH:MM - HH:MM", representing the start and end time of the slot.
   * The function starts at 09:00 and creates slots up to but not including 17:00 (last slot ends at 16:30).
   *
   * @returns {string[]} An array of formatted time slot strings.
   */
  const generateTimeSlots = () => {
    const slots = [];
    let start = 9 * 60; // Start time in minutes
    const end = 16 * 60 + 30; // End time in minutes

    while (start < end) {
      const hours = Math.floor(start / 60);
      const minutes = start % 60;
      const nextSlot = start + 30;

      const nextHours = Math.floor(nextSlot / 60);
      const nextMinutes = nextSlot % 60;

      const slot = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")} - ${nextHours
        .toString()
        .padStart(2, "0")}:${nextMinutes.toString().padStart(2, "0")}`;

      slots.push(slot);
      start = nextSlot;
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    /**
     * Fetches the list of disabled time slots for the currently selected date from the "Enquiries" collection in Firestore.
     * If no date is selected, clears the disabled slots.
     * Updates the state with the array of disabled time slots.
     *
     * @async
     * @function fetchDisabledSlots
     * @returns {Promise<void>} Resolves when the disabled slots have been fetched and state updated.
     */
    const fetchDisabledSlots = async () => {
      if (!selectedDate) {
        setDisabledSlots([]);
        return;
      }

      try {
        const callsQuery = query(
          collection(db, "Enquiries"),
          where("date", "==", selectedDate)
        );
        const querySnapshot = await getDocs(callsQuery);
        const disabledTimes = querySnapshot.docs
          .map((doc) => doc.data().time)
          .filter((time) => time !== null);

        setDisabledSlots(disabledTimes);
      } catch (error) {
        // console.error("Error fetching disabled time slots:", error);
      }
    };

    fetchDisabledSlots();
  }, [selectedDate]);

  /**
   * Handles the selection of a time slot.
   * If the selected time is not in the list of disabled slots, it sets the selected time
   * and closes the time selector.
   *
   * @param {string} time - The time slot selected by the user.
   */
  const handleTimeClick = (time) => {
    if (!disabledSlots.includes(time)) {
      setSelectedTime(time); // Only pass valid time selections
      onClose();
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {" "}
      {/* Wrapper for relative positioning */}
      <div className="Overlay" onClick={onClose}>
        <div className="Time-Selector" onClick={(e) => e.stopPropagation()}>
          <CloseIcon className="close-icon Content" onClick={onClose} />
          <div className="Time-Slot-Info">
            {!selectedDate ? (
              <div className="Content-Small">
                Please select a date to view available time slots.
              </div>
            ) : (
              <div className="Time-Slots-Container">
                {timeSlots.map((time, index) => (
                  <TimeSlots
                    key={index}
                    time={time}
                    onClick={() => handleTimeClick(time)}
                    disabled={disabledSlots.includes(time)}
                  />
                ))}
              </div>
            )}
            <div className="Content-Small">
              If you can’t find a suitable time, please fill out the contact us
              with your available times and we will arrange a time that works
              for you.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSelector;
